import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getGetDebtorTimelineQueryKey } from 'api';
import type { AxiosError, AxiosInstance } from 'axios';
import type { z } from 'zod/v4';

import { type ClientFormSchema, ClientFormSchemaSnakeCase } from './use-create-client';
import { type ApiResponse, addActionNameToMutationResult, useLegacyAxiosInstance } from './utils';

export type UpdateClientInformationForm = z.infer<typeof ClientFormSchema>;

export async function updateClientInformationFn(
  axiosInstance: Promise<AxiosInstance>,
  { id, data }: { id: string; data: UpdateClientInformationForm },
) {
  const instance = await axiosInstance;
  const snakeCaseData = ClientFormSchemaSnakeCase.parse(data);

  return instance.put(`/debtors/${id}`, snakeCaseData);
}

export function useUpdateClientInformation() {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    { id: string; data: UpdateClientInformationForm }
  >({
    mutationFn: variables => updateClientInformationFn(axiosInstance, variables),
    onSuccess: (_data, { id }) => {
      client.invalidateQueries({ queryKey: getGetDebtorTimelineQueryKey(id) });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    { id: string; data: UpdateClientInformationForm },
    'updateClientInfo'
  >('updateClientInfo', mutation);
}
